import { PlayoffBracket, PlayoffMatchup } from '../playoffs/playoff.model';

type BracketForVisibility = Pick<PlayoffBracket, 'matchups'>;

function containsTeam(matchup: PlayoffMatchup, teamId: string): boolean {
  return matchup.home_team_id === teamId || matchup.away_team_id === teamId;
}

function topologyKey(round: number, position: number): string {
  return `${round}:${position}`;
}

function hasValidTopology(matchup: PlayoffMatchup): boolean {
  return Number.isInteger(matchup.round) && matchup.round >= 1 &&
    Number.isInteger(matchup.position) && matchup.position >= 0;
}

/**
 * Returns the displayable playoff matchups for the selected team's reached and
 * still-possible downstream path. Bye nodes participate in traversal but are
 * never returned for display.
 */
export function tournamentVisiblePlayoffMatchupIds(
  bracket: BracketForVisibility,
  selectedTeamId?: string | null,
): Set<string> {
  const matchups = bracket.matchups || [];
  if (!selectedTeamId) {
    return new Set(matchups.filter((matchup) => !matchup.is_bye).map((matchup) => matchup.id));
  }

  const matchupByTopology = new Map<string, PlayoffMatchup>();
  const ambiguousTopology = new Set<string>();
  matchups.filter(hasValidTopology).forEach((matchup) => {
    const key = topologyKey(matchup.round, matchup.position);
    if (matchupByTopology.has(key)) ambiguousTopology.add(key);
    else matchupByTopology.set(key, matchup);
  });

  const selectedTeamMatchups = matchups.filter((matchup) => containsTeam(matchup, selectedTeamId));
  if (selectedTeamMatchups.length === 0) return new Set();

  if (selectedTeamMatchups.length > 1 && selectedTeamMatchups.some((matchup) => !hasValidTopology(matchup))) {
    return new Set();
  }
  const earliestRound = selectedTeamMatchups.length === 1
    ? selectedTeamMatchups[0].round
    : Math.min(...selectedTeamMatchups.map((matchup) => matchup.round));
  const startingMatchups = selectedTeamMatchups.length === 1
    ? selectedTeamMatchups
    : selectedTeamMatchups.filter((matchup) => matchup.round === earliestRound);
  if (startingMatchups.length !== 1) return new Set();

  const visibleIds = new Set<string>();
  const visited = new Set<PlayoffMatchup>();
  let current: PlayoffMatchup | undefined = startingMatchups[0];

  while (current && !visited.has(current)) {
    visited.add(current);
    if (!current.is_bye) visibleIds.add(current.id);

    if (current.winner_team_id !== null && current.winner_team_id !== selectedTeamId) break;
    if (!hasValidTopology(current)) break;
    if (ambiguousTopology.has(topologyKey(current.round, current.position))) break;

    const nextKey = topologyKey(current.round + 1, Math.floor(current.position / 2));
    if (ambiguousTopology.has(nextKey)) break;

    const next = matchupByTopology.get(nextKey);
    if (!next) break;

    const destinationTeamId = current.position % 2 === 0 ? next.home_team_id : next.away_team_id;
    const nextContainsTeam = containsTeam(next, selectedTeamId);
    if (destinationTeamId !== null && destinationTeamId !== selectedTeamId) break;
    if (destinationTeamId === null && nextContainsTeam) break;
    if (next.winner_team_id !== null && next.winner_team_id !== selectedTeamId && !nextContainsTeam) break;

    current = next;
  }

  return visibleIds;
}
